import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  EmptyState,
  Field,
  FilterBar,
  FilterSearch,
  InlineStats,
  Input,
  Modal,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import {
  IconBan,
  IconCheck,
  IconInfo,
  IconMerchants,
  IconPlus,
  IconRefresh
} from '../../components/admin/icons';
import { formatNumber } from '../../components/admin/labels';
import {
  billingLabel,
  billingTone,
  pilotPreviouslyUsed
} from '../../components/admin/plans';
import { fieldErrorsFrom } from '../../utils/formErrors';

const emptyForm = {
  name: '',
  email: '',
  accountManager: '',
  // The first login, created straight after the merchant itself. Not
  // optional: a merchant nobody can sign into is only half-created.
  loginName: '',
  loginEmail: '',
  loginPassword: ''
};

/** Compact "used / allowed" meter used for both seats and case allowance. */
const UsageMeter = ({ used, total }) => {
  if (total == null) {
    return <span className="tabular-nums text-ink-900">{formatNumber(used ?? 0)}</span>;
  }
  const pct = total > 0 ? Math.min(100, Math.round(((used || 0) / total) * 100)) : 0;
  const over = (used || 0) > total;
  return (
    <div className="min-w-[7rem]">
      <p className="text-sm tabular-nums text-ink-900">
        <span className={over ? 'font-600 text-warning-700' : 'font-600'}>{formatNumber(used ?? 0)}</span>
        <span className="text-ink-500"> / {formatNumber(total)}</span>
      </p>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
        <div
          className={`h-full rounded-[4px] ${over ? 'bg-warning-600' : 'bg-brand-600'}`}
          style={{ width: `${over ? 100 : pct}%` }}
        />
      </div>
    </div>
  );
};

const MerchantManagement = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();

  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [pricing, setPricing] = useState(null);

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/merchants');
      setMerchants(response.data);
    } catch (error) {
      toast('Could not load merchants.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Plan pricing for the create dialog. Read from the public pricing endpoint
   * because it returns plans already normalised (legacy number-only values and
   * quote-only plans included) — /api/admin/config returns the raw values and
   * would mean repeating that normalisation here.
   */
  const fetchPricing = useCallback(async () => {
    try {
      const { data } = await api.get('/api/merchants/pricing');
      setPricing(data);
    } catch {
      setPricing(null);
    }
  }, []);

  useEffect(() => {
    fetchMerchants();
    fetchPricing();
  }, [fetchMerchants, fetchPricing]);

  // Re-fetch on every open, not just on mount — otherwise an admin who edits
  // pricing on the Pricing & Plans page while this tab stays open keeps
  // seeing whatever prices were current when this page first loaded.
  const openCreate = () => {
    fetchPricing();
    setShowCreate(true);
  };

  const handleCreate = async () => {
    const found = {};
    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim();

    if (!trimmedName) found.name = 'Enter the merchant name.';
    if (!trimmedEmail) found.email = 'Enter an email address.';
    else if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) found.email = 'Enter a valid email address.';

    // Checked against the merchant list already loaded for the table, so this
    // catches the common case instantly rather than waiting on a round trip —
    // the server still has the authoritative check for a merchant created by
    // someone else since this list was fetched.
    if (trimmedName && !found.name) {
      const nameClash = merchants.some((m) => m.name?.trim().toLowerCase() === trimmedName.toLowerCase());
      if (nameClash) found.name = 'A merchant with this name already exists.';
    }
    if (trimmedEmail && !found.email) {
      const emailClash = merchants.some((m) => m.email?.toLowerCase() === trimmedEmail.toLowerCase());
      if (emailClash) found.email = 'A merchant with this email already exists.';
    }

    const loginEmail = (form.loginEmail || form.email).trim();
    if (!form.loginName.trim()) found.loginName = 'Enter the person’s name.';
    if (!loginEmail) found.loginEmail = 'Enter their email address.';
    else if (!/^\S+@\S+\.\S+$/.test(loginEmail)) found.loginEmail = 'Enter a valid email address.';
    if (!form.loginPassword) found.loginPassword = 'Enter a password.';
    else if (form.loginPassword.length < 8) found.loginPassword = 'Use at least 8 characters.';

    if (Object.keys(found).length) {
      setCreateErrors(found);
      return;
    }
    setCreateErrors({});

    setCreating(true);
    try {
      // Always a pilot: a paid plan has to be paid for, and nothing here can
      // take a card. The merchant moves onto one themselves from their own
      // portal, where Stripe collects payment.
      const { data } = await api.post('/api/admin/merchants', {
        name: form.name.trim(),
        email: form.email.trim(),
        plan: 'pilot',
        startPilot: true,
        accountManager: form.accountManager.trim() || undefined
      });
      // The login is a second call: there is no transaction, so a failure here
      // must be reported as exactly what it is — merchant created, login not.
      try {
        const { data: user } = await api.post(`/api/admin/merchants/${data._id}/users`, {
          name: form.loginName.trim(),
          email: (form.loginEmail || form.email).trim(),
          merchantRole: 'admin',
          password: form.loginPassword
        });

        toast(`${data.name} created, and ${user.email} can sign in now.`, { tone: 'success' });
      } catch (loginError) {
        toast(
          `${data.name} was created, but the login could not be added: ${
            loginError.response?.data?.error || 'unknown error'
          }`,
          { tone: 'error' }
        );
      }

      setShowCreate(false);
      setForm(emptyForm);
      navigate(`/admin/merchants/${data._id}`);
    } catch (error) {
      setCreateErrors(
        fieldErrorsFrom(error, { fallback: 'Could not create the merchant.' })
      );
    } finally {
      setCreating(false);
    }
  };

  const handleSuspend = async (merchant, suspend) => {
    const ok = await confirm({
      title: suspend ? 'Suspend merchant' : 'Restore merchant',
      message: suspend
        ? `Suspend ${merchant.name}? They will lose access until restored.`
        : `Restore access for ${merchant.name}?`,
      tone: suspend ? 'danger' : 'primary',
      confirmLabel: suspend ? 'Suspend' : 'Restore'
    });
    if (!ok) return;

    try {
      await api.patch(`/api/admin/merchants/${merchant._id}/suspend`, { suspended: suspend });
      fetchMerchants();
      toast(suspend ? `${merchant.name} suspended.` : `${merchant.name} restored.`, { tone: 'success' });
    } catch (error) {
      toast('Error updating merchant status.', { tone: 'error' });
    }
  };

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return merchants;
    return merchants.filter(
      (m) => m.name?.toLowerCase().includes(term) || m.email?.toLowerCase().includes(term)
    );
  }, [merchants, query]);

  return (
    <>
      <Card>
        {/* The topbar already titles this page, so the card opens straight
            into the controls for it. */}
        <FilterBar
          actions={
            <>
              <InlineStats
                items={[
                  {
                    label: query ? 'Matching' : 'Merchants',
                    value: loading ? '—' : formatNumber(filtered.length)
                  }
                ]}
              />
              <Button variant="secondary" size="sm" onClick={fetchMerchants} loading={loading}>
                <IconRefresh size={15} />
                Refresh
              </Button>
              <Button size="sm" onClick={openCreate}>
                <IconPlus size={15} />
                New merchant
              </Button>
            </>
          }
        >
          <FilterSearch
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or email"
            className="min-w-[16rem]"
          />
        </FilterBar>

        {loading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={IconMerchants}
            title={query ? 'No merchants match that search' : 'No merchants yet'}
            description={
              query
                ? 'Check the spelling, or clear the search to see every account.'
                : 'Create a merchant to start a pilot or put them on a paid plan.'
            }
            action={
              query ? (
                <Button variant="secondary" size="sm" onClick={() => setQuery('')}>
                  Clear search
                </Button>
              ) : (
                <Button size="sm" onClick={openCreate}>
                  <IconPlus size={15} />
                  New merchant
                </Button>
              )
            }
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Merchant</Th>
                <Th>Plan</Th>
                <Th>Status</Th>
                <Th>Seats used</Th>
                <Th title="Cases counted this cycle against the plan allowance.">Cases this cycle</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((merchant) => (
                <Tr key={merchant._id}>
                  <Td>
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/merchants/${merchant._id}`)}
                      className="rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                    >
                      <span className="block font-600 text-brand-700 hover:underline">
                        {merchant.name}
                      </span>
                      <span className="block text-xs text-ink-500">{merchant.email}</span>
                    </button>
                  </Td>
                  <Td>
                    <StatusPill tone={billingTone(merchant)}>{billingLabel(merchant)}</StatusPill>
                    {merchant.isTrialActive && merchant.trial?.endAt && (
                      <span className="mt-1 block text-xs text-ink-500">
                        Trial ends {new Date(merchant.trial.endAt).toLocaleDateString('en-GB')}
                      </span>
                    )}
                    {!merchant.isTrialActive && pilotPreviouslyUsed(merchant) && (
                      <span className="mt-1 block text-xs text-ink-500">Pilot used</span>
                    )}
                  </Td>
                  <Td>
                    <StatusPill
                      tone={merchant.isSuspended ? 'danger' : merchant.isActive ? 'success' : 'neutral'}
                    >
                      {merchant.isSuspended ? 'Suspended' : merchant.isActive ? 'Active' : 'Inactive'}
                    </StatusPill>
                  </Td>
                  <Td>
                    <UsageMeter used={merchant.usersCount} total={merchant.usersAllowed} />
                  </Td>
                  <Td>
                    <UsageMeter
                      used={merchant.caseCountThisCycle}
                      total={
                        merchant.includedCases ??
                        (merchant.billingModel === 'free' ? merchant.freeCaseAllowance : null)
                      }
                    />
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/admin/merchants/${merchant._id}`)}
                      >
                        View
                      </Button>
                      <Button
                        variant={merchant.isSuspended ? 'secondary' : 'dangerGhost'}
                        size="sm"
                        onClick={() => handleSuspend(merchant, !merchant.isSuspended)}
                      >
                        {merchant.isSuspended ? <IconCheck size={15} /> : <IconBan size={15} />}
                        {merchant.isSuspended ? 'Restore' : 'Suspend'}
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setCreateErrors({});
        }}
        title="New merchant"
        description="Create the account, then add their team and set allowances."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={creating}>
              Create merchant
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {createErrors._general && (
            <div
              role="alert"
              className="rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-700 ring-1 ring-inset ring-danger-100"
            >
              {createErrors._general}
            </div>
          )}

          {/* Merchant details */}
          <section>
            <h3 className="font-display text-xs font-700 uppercase tracking-[0.08em] text-ink-500">
              Merchant details
            </h3>

            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <Field label="Merchant name" htmlFor="new-name" required error={createErrors.name}>
                <Input
                  id="new-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ABC Fashion"
                  autoFocus
                />
              </Field>

              <Field
                label="Email"
                htmlFor="new-email"
                required
                error={createErrors.email}
                hint="Used for case notifications and billing."
              >
                <Input
                  id="new-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="support@abc.co.uk"
                />
              </Field>

              <Field
                label="Account manager"
                htmlFor="new-am"
                hint="Optional — who owns this account internally."
                className="sm:col-span-2"
              >
                <Input
                  id="new-am"
                  value={form.accountManager}
                  onChange={(e) => setForm({ ...form, accountManager: e.target.value })}
                  placeholder="George"
                />
              </Field>
            </div>
          </section>

          {/* Plan — not a choice here. A paid plan has to be paid for, and
              this form cannot take a card, so every merchant created from
              admin starts on the pilot and upgrades themselves from their
              own portal where Stripe can collect. */}
          <section className="border-t border-ink-100 pt-4">
            <h3 className="font-display text-xs font-700 uppercase tracking-[0.08em] text-ink-500">
              Plan
            </h3>
            <div className="mt-3 flex items-start gap-3 rounded-xl bg-ink-50 px-4 py-3.5">
              <IconInfo size={17} className="mt-0.5 shrink-0 text-ink-500" />
              <p className="text-sm leading-relaxed text-ink-700">
                Starts on the <span className="font-600 text-ink-900">Pilot</span> plan
                {pricing?.merchantPlanPrices?.pilot?.includedCases != null && (
                  <>
                    {' '}— {formatNumber(pricing.merchantPlanPrices.pilot.includedCases)} free cases,
                    no time limit
                  </>
                )}
                . Paid plans are bought by the merchant from their own portal, so they can enter
                payment details.
              </p>
            </div>
          </section>

          {/* Access — always created. A merchant with no login can't sign in
              to use the pilot they were just given, which made the old
              opt-out the wrong default rather than a real choice. */}
          <section className="border-t border-ink-100 pt-4">
            <h3 className="font-display text-xs font-700 uppercase tracking-[0.08em] text-ink-500">
              Admin login
              <span className="ml-0.5 text-danger-600" aria-hidden="true">*</span>
              <span className="sr-only"> (required)</span>
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-ink-500">
              Created with the merchant so they can sign in straight away. Share the password with
              them — they can change it once they're in.
            </p>

            <div className="mt-4 grid gap-4 rounded-xl bg-ink-50 p-4 sm:grid-cols-2">
                <Field
                  label="Their name"
                  htmlFor="new-login-name"
                  required
                  error={createErrors.loginName}
                >
                  <Input
                    id="new-login-name"
                    value={form.loginName}
                    onChange={(e) => setForm({ ...form, loginName: e.target.value })}
                    placeholder="Sarah Green"
                  />
                </Field>

                <Field
                  label="Login email"
                  htmlFor="new-login-email"
                  required
                  error={createErrors.loginEmail}
                >
                  <Input
                    id="new-login-email"
                    type="email"
                    value={form.loginEmail}
                    onChange={(e) => setForm({ ...form, loginEmail: e.target.value })}
                    placeholder={form.email || 'sarah@abc.co.uk'}
                  />
                </Field>

                <Field
                  label="Password"
                  htmlFor="new-login-password"
                  required
                  error={createErrors.loginPassword}
                  className="sm:col-span-2"
                >
                  <Input
                    id="new-login-password"
                    type="text"
                    value={form.loginPassword}
                    onChange={(e) => setForm({ ...form, loginPassword: e.target.value })}
                    placeholder="Minimum 8 characters"
                    autoComplete="new-password"
                  />
                </Field>
            </div>
          </section>
        </div>
      </Modal>
    </>
  );
};

export default MerchantManagement;
